import React, { useEffect } from 'react'
import { Link, useLocation, useRouteMatch } from 'react-router-dom'
import { BreadcrumbView } from '../../../../../context/breadcrumb.context'
import { useApiListOrgSso, useApiFormOrgSso, useApiListOrgSsoConsentRequest } from '../../../../../api-new/state/auth'
import { useRoleOrgSSO } from '../../../../../role/hook/role-org'
import { useFeatureSSO } from '../../../../../shared/component/org-feature'
import { IconShield } from '../../../../../shared/icon/icon'
import { useQueryParam } from '../../../../../shared/hooks/location'
import { WorkspaceSection } from '../../../../../shared/sso-workspace/workspace-shell'
import { connectionUse, ConnectionUsePill } from '../../../../../shared/sso-workspace/connection-use'
import { ProviderLogo } from '../../../../../shared/sso-workspace/provider-logo'
import { ConnectProvider } from '../../../../../shared/sso-consent/connect-provider'
import { ConsentRequestList } from '../../../../../shared/sso-consent/consent-request-list'
import '../../../../../shared/sso-consent/sso-consent.scss'
import { OrgSsoDomain } from './org-sso-domain'
import { OrgSsoRecipient } from './org-sso-recipient'
import { OrgSsoProvisioning } from './org-sso-provisioning'
import { SectionNav, scrollToSection } from '../../../../../shared/sso-workspace/section-nav'
import { Pill } from '../../../../../shared/sso-workspace/pill'
import { SCIM_TITLE } from '../../../../../shared/scim/scim-words'
import { useScimLinks } from '../../../../../shared/scim/use-scim-links'
import { useAuthContext } from '../../../../../context/auth.context'
import { useAuditRecord } from '../../../../../shared/audit/history/audit-record-context'

// What kind of connection a row is, in words (never the raw driver /
// connection fields). An app registration of its own is named only when it
// is the organization's (OWN_REGISTRATION_MANAGED): one EducationXR runs on
// a registration of its own is just "Managed by EducationXR".
export const CONNECTION_KIND = {
  shared_app: "EducationXR's shared app"
}

const connectionName = connection => connection.displayName || connection.providerName

// Whether the organization runs the connection itself: auth's selfServe (its
// own, on EducationXR's shared app); an older auth sends only owned.
export const managedByOrganization = connection =>
  typeof connection.selfServe === 'boolean' ? connection.selfServe : Boolean(connection.owned)

// The organization's own connection on its own app registration: it owns the
// connection, but EducationXR runs it (owner ruling 2026-10-24).
export const OWN_REGISTRATION_MANAGED = "Your organization's own app registration · credentials managed by EducationXR"
const ownRegistrationManaged = connection =>
  connection.connection === 'own_registration' && Boolean(connection.owned) && !managedByOrganization(connection)

// The row's subtitle: what kind of connection it is and who runs it.
export const connectionKindLine = connection =>
  ownRegistrationManaged(connection)
    ? OWN_REGISTRATION_MANAGED
    : [CONNECTION_KIND[connection.connection], managedByOrganization(connection) ? 'Managed by your organization' : 'Managed by EducationXR'].filter(Boolean).join(' · ')

// The connection page's note when the organization can't change it.
export const managedNote = connection =>
  `${ownRegistrationManaged(connection) ? OWN_REGISTRATION_MANAGED : 'Managed by EducationXR'}. Contact support to change it.`

// The organization's Authentication page, in the connection workspace's
// language (spec 2026-09-26 design pass, review I5). Plan-gated on
// featureSSO: without it the organization sees one notice, since every
// org-side SSO endpoint refuses anyway -- and, when one of its connections
// still has a SCIM token, a revoke-only Provisioning (SCIM) section
// (PlanOffProvisioning: revoking never needs the plan, spec R3). With it:
//
//   - Connections: every connection linked to the organization, owned or
//     not (audit B6), with its kind, the domains it signs in and its status.
//     One EducationXR manages is labelled so, with who to ask.
//   - Provisioning (SCIM): the organization's own links, turn on, rotate,
//     revoke, activity and the Entra / Okta setup guide
//     (org-sso-provisioning.js; content's /scim/connections, spec R22).
//   - Domains: claim, verify (TXT record), require SSO, bind and unbind
//     (org-sso-domain.js).
//   - Each connection's own page (/org/sso/connections/:id, org-connection-
//     workspace.js): its test addresses, status, names, sign-out and help
//     when the organization runs it itself.
//   - Alert recipients (org-sso-recipient.js).
//   - Connect a sign-in provider and Pending approvals (the same
//     ConnectProvider and ConsentRequestList the staff pages use, scoped to
//     the caller's own organization -- the backend takes it from the JWT).
//
// Every action follows ORG_SSO, the permission the server checks (B8).
export const OrgSso = () => {
  const featureSSO = useFeatureSSO()
  const authContext = useAuthContext()

  // History here is the organization's, with its domains, connections and
  // alert recipients (its child records); the page shows no one connection
  // of its own. No editor role: content opens an organization's History
  // only to audit-log readers (core event.RecordScope has no entry for
  // organizations), so ORG_SSO alone would only be refused.
  useAuditRecord({ type: 'organizations', id: authContext.state.organizationID, children: true })

  if (!featureSSO) {
    return (
      <BreadcrumbView Icon={IconShield} text="Authentication">
        <div className="sw-workspace sw-pane">
          <WorkspaceSection id="plan" title="Single sign-on">
            <p className="mb-0">Single sign-on is not included in your organization's plan. Contact EducationXR to enable it.</p>
          </WorkspaceSection>
          <PlanOffProvisioning />
        </div>
      </BreadcrumbView>
    )
  }

  return <OrgSsoContent />
}

// Plan off (R3): the organization's links that still have a SCIM token
// (content's GET /scim/connections answers without the plan), so it can
// revoke them; nothing at all when none has one.
const PlanOffProvisioning = () => {
  const roleOrgSSO = useRoleOrgSSO()
  const scim = useScimLinks()
  const withToken = Array.isArray(scim.links) ? scim.links.filter(l => l && l.token) : []
  if (withToken.length === 0) return null
  return (
    <WorkspaceSection id="provisioning" title={SCIM_TITLE}
      lede="Single sign-on isn't in your organization's plan, so provisioning can't be turned on or its token rotated. You can still revoke a token: your identity provider's requests stop, and the people it provisioned stay as they are.">
      <OrgSsoProvisioning connections={[]} enabled={roleOrgSSO.accessUpdate} revokeOnly scim={{ ...scim, links: withToken }} />
    </WorkspaceSection>
  )
}

const OrgSsoContent = () => {
  const apiListOrgSso = useApiListOrgSso()
  const orgSso = useApiFormOrgSso()

  // ORG_SSO, the permission every /org-sso endpoint checks (audit B8).
  const roleOrgSSO = useRoleOrgSSO()

  const apiListConsentRequest = useApiListOrgSsoConsentRequest()
  const initialTenant = useQueryParam('tenant') || ''

  // Loaded whenever the page itself is shown (BreadcrumbView onMatch): on
  // arrival, and again on the way back from a connection's page, whose
  // changes the rows then show (review L1). A connection's page, nested
  // under this route, loads nothing of this page's.
  const loadLists = () => Promise.all([apiListOrgSso.get(), apiListConsentRequest.get()])

  const handleRefresh = async () => await apiListOrgSso.get()
  const handleRefreshRequests = async () => await apiListConsentRequest.get()

  const handleRequest = ({ tenant, sendTo }) => orgSso.postArg('/entra/request', { tenant, sendTo })
  const handleResend = async id => { const result = await orgSso.postArg(`/consent-requests/${id}/resend`, {}); await handleRefreshRequests(); return result }
  const handleCancel = async id => { const result = await orgSso.postArg(`/consent-requests/${id}/cancel`); await handleRefreshRequests(); return result }

  const handleGoogle = async ({ domain, name }) => {
    await orgSso.postArg('/google', { domain, providerName: name, displayName: name })
    await handleRefresh()
  }

  const connections = apiListOrgSso.items

  // A #section in the address (a connection page links to #domains) scrolls
  // there once the connections have loaded.
  const { hash } = useLocation()
  const exact = Boolean(useRouteMatch({ path: '/org/sso', exact: true }))
  const listed = connections.length
  useEffect(() => { if (hash && exact) scrollToSection(hash.slice(1)) }, [hash, listed, exact])

  return (
    <BreadcrumbView Icon={IconShield} text="Authentication" onMatch={loadLists}>
      {/* The page is taller than the pane it sits in: it scrolls itself
          (the layout around it is overflow-hidden). */}
      <div className="sw-workspace sw-pane">
        <header className="mb-3">
          <h1 className="sw-title">Single sign-on</h1>
          <div className="sw-sub">Let people at your organization sign in to EducationXR with the work accounts they already use.</div>
        </header>

        <div className="sw-layout">
        <SectionNav sections={ORG_SSO_SECTIONS} mode="customer" />
        <main>

        <WorkspaceSection id="connections" title="Connections" lede="The identity providers people here sign in through.">
          {connections.length === 0
            ? <div className="sc-empty-state">
              <p>No connections yet. Connect Microsoft or Google below so people here can sign in with their work accounts.</p>
            </div>
            : <ul className="sc-rows">
              {connections.map(connection => {
                // Switched on, not public, and no domain of ANY organization
                // bound (boundDomainCount): nobody is sent to it.
                const use = connectionUse({ active: connection.active, isPublic: connection.public, bound: connection.boundDomainCount, testers: Array.isArray(connection.testAllowlist) ? connection.testAllowlist.length : undefined })
                return (
                  <li key={connection.id} className="sc-row" data-testid={`connection-${connection.id}`}>
                    <ProviderLogo iconSVG={connection.iconSVG} iconSVGDark={connection.iconSVGDark} />
                    <div className="sc-row-main">
                      <Link className="fw-semibold" to={`/org/sso/connections/${connection.id}`}>{connectionName(connection)}</Link>
                      <span className="sw-hint">
                        {connectionKindLine(connection)}
                      </span>
                      {/* Not in use says why, which also covers "none of yours". */}
                      <span className="sw-hint">
                        {use.reason ||
                          ((connection.domains || []).length > 0
                            ? `Signs in people at ${connection.domains.map(d => d.domain).join(', ')}.`
                            : 'No domains of yours are bound to it yet.')}
                      </span>
                      {/* The subtitle already says EducationXR manages it: once is enough. */}
                      {!managedByOrganization(connection) && <span className="sw-hint">Contact support to change it.</span>}
                    </div>
                    {/* auth's scim flag (spec R22): provisioning is on for it; absent on an older auth */}
                    {connection.scim && <Pill tone="info" title="Your identity provider adds and removes people through provisioning (SCIM).">Provisioning</Pill>}
                    <ConnectionUsePill use={use} to={`/org/sso/connections/${connection.id}#testing`} LinkComponent={Link} />
                  </li>
                )
              })}
            </ul>}
        </WorkspaceSection>

        <WorkspaceSection id="provisioning" title={SCIM_TITLE}
          lede="Let your identity provider add, update and remove your people in EducationXR through SCIM 2.0, for example Microsoft Entra ID or Okta.">
          <OrgSsoProvisioning connections={connections} enabled={roleOrgSSO.accessUpdate} />
        </WorkspaceSection>

        <WorkspaceSection id="domains" title="Domains"
          lede="The email domains your organization has claimed. Verify a domain, then bind it to a connection to send its people to single sign-on.">
          <OrgSsoDomain connections={connections} enabled={roleOrgSSO.accessUpdate} onConnectionsChanged={handleRefresh} />
        </WorkspaceSection>

        <WorkspaceSection id="recipients" title="Alert recipients"
          lede="Who hears about your single sign-on alerts: domains at risk or deactivated, and expiring credentials on connections your organization owns.">
          <OrgSsoRecipient enabled={roleOrgSSO.accessUpdate} />
        </WorkspaceSection>

        <WorkspaceSection id="connect" title="Connect a sign-in provider"
          lede="Let people at your organization sign in to EducationXR with the accounts they already use.">
          <ConnectProvider
            audience="org"
            enabled={roleOrgSSO.accessUpdate}
            initialTenant={initialTenant}
            onRequest={handleRequest}
            onRequested={handleRefreshRequests}
            onResend={handleResend}
            onCancel={handleCancel}
            onGoogle={handleGoogle}
          />
        </WorkspaceSection>

        <WorkspaceSection id="approvals" title="Pending approvals"
          lede="Requests sent to your IT administrator. Each link works for 7 days; sending a new one for the same Microsoft organization replaces the old link.">
          <ConsentRequestList
            rows={apiListConsentRequest.items}
            loaded={apiListConsentRequest.loaded !== false}
            enabled={roleOrgSSO.accessUpdate}
            initialFilter="pending"
            emptyText="No approval requests yet. Send one above and it shows here until your IT administrator approves it."
            onLink={id => orgSso.get(`consent-requests/${id}/link`)}
            onResend={id => orgSso.postArg(`/consent-requests/${id}/resend`, {})}
            onCancel={id => orgSso.postArg(`/consent-requests/${id}/cancel`)}
            onChanged={handleRefreshRequests}
          />
        </WorkspaceSection>
        </main>
        </div>
      </div>
    </BreadcrumbView>
  )
}

// The Authentication page's own sections, in page order, for its side nav
// (the connection workspace's SectionNav: it highlights the one in view).
export const ORG_SSO_SECTIONS = [
  { id: 'connections', label: 'Connections' },
  { id: 'provisioning', label: SCIM_TITLE },
  { id: 'domains', label: 'Domains' },
  { id: 'recipients', label: 'Alert recipients' },
  { id: 'connect', label: 'Connect a sign-in provider' },
  { id: 'approvals', label: 'Pending approvals' }
]
